// Schema introspection runs from the HOST realm (sidebar tree, SQL
// completions, the results grid) on behalf of an extension, so it goes
// through the same grant check the guest RPC path uses — see
// capabilities/enforce.js (EXT-05a).
import { invokeAsExtension as invoke } from './capabilities/enforce';
import { useWorkspaceStore } from '../store/workspaceStore';
import { statementRangeAtOffset } from '../components/dbSqlStatements';

/**
 * Shared, host-side engine metadata + schema catalog for DB-backed extension
 * features. Single source of truth for the per-engine Tauri command names,
 * defaults, and identifier/placeholder dialect quirks that were previously
 * duplicated between DbManagerResultsTab and the DB Manager guest bundle —
 * host features (tab reconnect, SQL completions) import from here; the guest
 * keeps its own copy by necessity (it can't import host modules).
 */

export const DB_QUERY_COMMANDS = {
  mysql: 'db_mysql_query',
  postgres: 'db_postgres_query',
  sqlite: 'db_sqlite_query',
};

// Multi-result-set variants of the same commands: identical args, but the
// return value is an ARRAY of { columns, rows, affectedRows, lastInsertId } —
// one element per statement in execution order (row-less statements produce
// entries with empty columns/rows and their affectedRows). Used by free-form
// query tabs so multi-statement scripts show every result, not just the last;
// table-browse tabs keep the single-result commands.
export const DB_QUERY_MULTI_COMMANDS = {
  mysql: 'db_mysql_query_multi',
  postgres: 'db_postgres_query_multi',
  sqlite: 'db_sqlite_query_multi',
};

/**
 * Row ceiling for free-form SQL, enforced in Rust.
 *
 * The table-browse path is already bounded by its own LIMIT/OFFSET, but the
 * query editor sends whatever the user typed — and `SELECT * FROM events` on a
 * large table used to be materialized three times over (a Vec of decoded cells
 * in Rust, the serialized IPC payload, then the parsed JS objects) before a
 * single row reached the screen. Matches the grid's MAX_RENDER_ROWS, so the
 * backend now stops where the grid was going to stop drawing anyway.
 */
/** Liveness check per engine — used by the health monitor, never by the grid. */
export const DB_PING_COMMANDS = {
  mysql: 'db_mysql_ping',
  postgres: 'db_postgres_ping',
  sqlite: 'db_sqlite_ping',
};

export const DEFAULT_QUERY_MAX_ROWS = 10000;

/**
 * Owner of the saved-SQL scripts, and therefore the id the `db:scripts` gate is
 * checked against. Host-side script UI acts on DB Manager's behalf the same way
 * the grid does for its queries.
 */
export const DB_MANAGER_EXTENSION_ID = 'com.apiboo.db-manager';

export const DB_CONNECT_COMMANDS = {
  mysql: 'db_mysql_connect',
  postgres: 'db_postgres_connect',
  sqlite: 'db_sqlite_connect',
};

// Atomic multi-statement execution on ONE pooled connection (real
// transaction) — see db_mysql_execute_batch in Rust for why BEGIN/COMMIT via
// separate query calls cannot work over a pool.
export const DB_BATCH_COMMANDS = {
  mysql: 'db_mysql_execute_batch',
  postgres: 'db_postgres_execute_batch',
  sqlite: 'db_sqlite_execute_batch',
};

export const DEFAULT_DB_PORTS = { mysql: 3306, postgres: 5432 };

export const DB_ENGINE_LABELS = { mysql: 'MySQL', postgres: 'PostgreSQL', sqlite: 'SQLite' };

export function quoteIdentFor(engine, name) {
  if (engine === 'mysql') return '`' + String(name).replace(/`/g, '``') + '`';
  return '"' + String(name).replace(/"/g, '""') + '"';
}

/**
 * Scripts are workspace-scoped on disk (scripts/db/<workspaceId>/...); every
 * db_script* invoke passes this. Null (no workspace) falls back to the
 * legacy shared root.
 */
export function currentScriptScope() {
  // Workspace ids are numeric — the Rust command takes Option<String> (it
  // becomes a directory name), so coerce; null falls back to the legacy root.
  const id = useWorkspaceStore.getState().currentWorkspace?.id;
  return id != null ? String(id) : null;
}

/**
 * Sidecar payload binding a saved script to a query tab's database — the one
 * shape both save paths (direct Ctrl+S and the save-as modal) must agree on.
 */
export function dbScriptMetaForTab(tab) {
  return {
    engine: DB_QUERY_COMMANDS[tab?.engine] ? tab.engine : 'mysql',
    dbName: tab?.dbName || null,
    extensionId: tab?.extensionId,
    connId: tab?.connId,
  };
}

/**
 * The statement at `offset` in a SQL script, or null. One implementation for
 * every splitter: `;` and empty lines end a statement (owner, 30.09.2026) —
 * see components/dbSqlStatements.js.
 */
export function statementAtOffset(text, offset, engine) {
  return statementRangeAtOffset(text, offset, { engine })?.sql ?? null;
}

/** MySQL/SQLite use `?`; Postgres `$n` — the closure keeps one counter. */
export function makePlaceholder(engine) {
  let n = 0;
  return () => {
    n += 1;
    return engine === 'postgres' ? `$${n}` : '?';
  };
}

// ---- Schema catalog ---------------------------------------------------------
// Cached table/column names per (extensionId, connId, dbName), used by SQL
// completions. Promises are cached (not results) so concurrent lookups share
// one query; a failed load evicts itself so the next lookup retries.

const catalog = new Map(); // key -> { tables: Promise<string[]>|null, columns: Map<table, Promise<string[]>> }
const SEP = '\u0000';

function catalogEntry(extensionId, connId, dbName) {
  const key = `${extensionId}${SEP}${connId}${SEP}${dbName || ''}`;
  let entry = catalog.get(key);
  if (!entry) {
    entry = { tables: null, columns: new Map(), foreignKeys: new Map() };
    catalog.set(key, entry);
  }
  return entry;
}

// Other catalog caches (the database overview's) that go stale with this one.
// A listener rather than an import: they import from here.
const schemaCacheListeners = new Set();
export function onSchemaCacheInvalidated(fn) {
  schemaCacheListeners.add(fn);
  return () => schemaCacheListeners.delete(fn);
}

/** Drop everything cached for a connection — call on (re)connect/disconnect. */
export function invalidateSchemaCache(extensionId, connId) {
  const prefix = `${extensionId}${SEP}${connId}${SEP}`;
  for (const key of [...catalog.keys()]) {
    if (key.startsWith(prefix)) catalog.delete(key);
  }
  schemaCacheListeners.forEach((fn) => fn(extensionId, connId));
}

async function queryFirstColumn(engine, extensionId, connId, sql, params) {
  const res = await invoke(DB_QUERY_COMMANDS[engine], { extensionId, connId, sql, params });
  return (res?.rows || []).map((row) => String(row[0])).filter(Boolean);
}

/**
 * Databases (MySQL) / schemas (Postgres) reachable on a live connection —
 * feeds the new-query-tab picker. System schemas are filtered out; SQLite has
 * exactly its one attached file.
 */
export function listDatabases(engine, extensionId, connId) {
  if (engine === 'sqlite') return Promise.resolve(['main']);
  if (engine === 'postgres') {
    return queryFirstColumn(
      engine,
      extensionId,
      connId,
      "SELECT schema_name FROM information_schema.schemata WHERE schema_name NOT LIKE 'pg_%' AND schema_name <> 'information_schema' ORDER BY schema_name",
      []
    );
  }
  return queryFirstColumn(
    engine,
    extensionId,
    connId,
    "SELECT schema_name FROM information_schema.schemata WHERE schema_name NOT IN ('information_schema', 'performance_schema', 'mysql', 'sys') ORDER BY schema_name",
    []
  );
}

export function listTables(engine, extensionId, connId, dbName) {
  const entry = catalogEntry(extensionId, connId, dbName);
  if (!entry.tables) {
    let sql;
    let params;
    if (engine === 'sqlite') {
      sql = "SELECT name FROM sqlite_master WHERE type = 'table' AND name NOT LIKE 'sqlite_%' ORDER BY name";
      params = [];
    } else if (engine === 'postgres') {
      sql = dbName
        ? 'SELECT table_name FROM information_schema.tables WHERE table_schema = $1 ORDER BY table_name'
        : 'SELECT table_name FROM information_schema.tables WHERE table_schema = current_schema() ORDER BY table_name';
      params = dbName ? [dbName] : [];
    } else {
      sql = dbName
        ? 'SELECT table_name FROM information_schema.tables WHERE table_schema = ? ORDER BY table_name'
        : 'SELECT table_name FROM information_schema.tables WHERE table_schema = DATABASE() ORDER BY table_name';
      params = dbName ? [dbName] : [];
    }
    entry.tables = queryFirstColumn(engine, extensionId, connId, sql, params).catch((err) => {
      entry.tables = null; // don't cache the failure
      throw err;
    });
  }
  return entry.tables;
}

export function listColumns(engine, extensionId, connId, dbName, table) {
  const entry = catalogEntry(extensionId, connId, dbName);
  let promise = entry.columns.get(table);
  if (!promise) {
    if (engine === 'sqlite') {
      // PRAGMA rows are [cid, name, type, notnull, dflt_value, pk].
      promise = invoke(DB_QUERY_COMMANDS.sqlite, {
        extensionId,
        connId,
        sql: `PRAGMA table_info(${quoteIdentFor('sqlite', table)})`,
        params: [],
      }).then((res) => (res?.rows || []).map((row) => String(row[1])).filter(Boolean));
    } else if (engine === 'postgres') {
      promise = queryFirstColumn(
        engine,
        extensionId,
        connId,
        dbName
          ? 'SELECT column_name FROM information_schema.columns WHERE table_schema = $1 AND table_name = $2 ORDER BY ordinal_position'
          : 'SELECT column_name FROM information_schema.columns WHERE table_schema = current_schema() AND table_name = $1 ORDER BY ordinal_position',
        dbName ? [dbName, table] : [table]
      );
    } else {
      promise = queryFirstColumn(
        engine,
        extensionId,
        connId,
        dbName
          ? 'SELECT column_name FROM information_schema.columns WHERE table_schema = ? AND table_name = ? ORDER BY ordinal_position'
          : 'SELECT column_name FROM information_schema.columns WHERE table_schema = DATABASE() AND table_name = ? ORDER BY ordinal_position',
        dbName ? [dbName, table] : [table]
      );
    }
    promise = promise.catch((err) => {
      entry.columns.delete(table);
      throw err;
    });
    entry.columns.set(table, promise);
  }
  return promise;
}

// Column types that read as a human-meaningful label. Used to pick what the
// FK dictionary shows next to a key: "1 — Samin Grahich" beats "1 — 1".
const TEXTUAL_COLUMN_TYPE_RE = /char|text|string|clob|enum|name/i;

/**
 * Which column(s) of a referenced table to show beside its key in the FK
 * dictionary. First text-ish non-key column, else the first non-key column,
 * else the key itself (a pure join table has nothing better to offer).
 *
 * Pure and exported so the choice is testable without a database.
 */
export function pickDisplayColumns(columnsMeta, keyColumn) {
  const usable = (columnsMeta || []).filter((c) => c && c.name && c.name !== keyColumn);
  const textual = usable.find((c) => TEXTUAL_COLUMN_TYPE_RE.test(String(c.type || '')));
  if (textual) return [textual.name];
  if (usable.length > 0) return [usable[0].name];
  return [keyColumn];
}

/**
 * Columns of `table` with their declared types and whether they are part of the
 * primary key: `[{ name, type, isPk }]`. Cached beside the name-only list,
 * which the SQL completions use and which cannot answer "which of these is a
 * label".
 */
export function listColumnsMeta(engine, extensionId, connId, dbName, table) {
  const entry = catalogEntry(extensionId, connId, dbName);
  if (!entry.columnsMeta) entry.columnsMeta = new Map();
  let promise = entry.columnsMeta.get(table);
  if (promise) return promise;

  if (engine === 'sqlite') {
    // PRAGMA rows: [cid, name, type, notnull, dflt_value, pk].
    promise = invoke(DB_QUERY_COMMANDS.sqlite, {
      extensionId,
      connId,
      sql: `PRAGMA table_info(${quoteIdentFor('sqlite', table)})`,
      params: [],
    }).then((res) =>
      (res?.rows || []).map((row) => ({
        name: String(row[1]),
        type: String(row[2] || ''),
        isPk: Number(row[5]) > 0,
      }))
    );
  } else {
    const isPg = engine === 'postgres';
    const p = (n) => (isPg ? `$${n}` : '?');
    promise = invoke(DB_QUERY_COMMANDS[isPg ? 'postgres' : 'mysql'], {
      extensionId,
      connId,
      sql: isPg
        ? `SELECT c.column_name, c.data_type,
             EXISTS (
               SELECT 1 FROM information_schema.table_constraints tc
               JOIN information_schema.key_column_usage k
                 ON k.constraint_name = tc.constraint_name AND k.table_schema = tc.table_schema
               WHERE tc.constraint_type = 'PRIMARY KEY' AND tc.table_schema = c.table_schema
                 AND tc.table_name = c.table_name AND k.column_name = c.column_name
             ) AS is_pk
           FROM information_schema.columns c
           WHERE c.table_schema = ${dbName ? p(1) : 'current_schema()'} AND c.table_name = ${dbName ? p(2) : p(1)}
           ORDER BY c.ordinal_position`
        : `SELECT COLUMN_NAME, DATA_TYPE, COLUMN_KEY = 'PRI'
           FROM information_schema.COLUMNS
           WHERE TABLE_SCHEMA = ${dbName ? '?' : 'DATABASE()'} AND TABLE_NAME = ?
           ORDER BY ORDINAL_POSITION`,
      params: dbName ? [dbName, table] : [table],
    }).then((res) =>
      (res?.rows || []).map((row) => ({
        name: String(row[0]),
        type: String(row[1] || ''),
        isPk: row[2] === true || row[2] === 1 || row[2] === '1',
      }))
    );
  }

  // Same rule as getForeignKeys: this is an enhancement, so a catalog we
  // cannot read costs the dictionary's labels, not the panel.
  promise = promise.catch(() => []);
  entry.columnsMeta.set(table, promise);
  return promise;
}

/**
 * Groups flat `{ name, column, refDb, refTable, refColumn }` rows — ordered by
 * constraint then by position within it — into one entry per foreign key.
 *
 * Exported for tests: the per-engine SQL needs a live database, but the
 * pairing does not, and getting the pairing wrong on a composite key is how
 * you build a WHERE clause that matches the wrong row.
 */
export function groupForeignKeyRows(rows) {
  const byName = new Map();
  for (const r of rows) {
    if (!r || !r.name || !r.column || !r.refTable) continue;
    let fk = byName.get(r.name);
    if (!fk) {
      fk = { name: r.name, refDb: r.refDb || null, refTable: r.refTable, columns: [], refColumns: [] };
      byName.set(r.name, fk);
    }
    fk.columns.push(r.column);
    fk.refColumns.push(r.refColumn);
  }
  // A key whose referenced columns didn't all resolve can't address a row.
  return [...byName.values()].filter(
    (fk) => fk.columns.length > 0 && fk.columns.length === fk.refColumns.filter(Boolean).length
  );
}

/**
 * Foreign keys declared ON `table`: `[{ name, refDb, refTable, columns,
 * refColumns }]`, where `columns[i]` references `refColumns[i]`.
 *
 * Cached and invalidated with the rest of the catalog, so a reconnect picks up
 * DDL changes. A failure resolves to [] rather than rejecting — foreign keys
 * are an enhancement to the grid, and a server that won't report them (or a
 * user without the rights to read the catalog) should cost the navigate button,
 * not the table.
 */
export function getForeignKeys(engine, extensionId, connId, dbName, table) {
  const entry = catalogEntry(extensionId, connId, dbName);
  let promise = entry.foreignKeys.get(table);
  if (promise) return promise;

  if (engine === 'sqlite') {
    // Rows: [id, seq, table, from, to, on_update, on_delete, match]. `to` is
    // NULL when the key references the target's implicit primary key, so those
    // are resolved from its table_info in a second pass.
    promise = invoke(DB_QUERY_COMMANDS.sqlite, {
      extensionId,
      connId,
      sql: `PRAGMA foreign_key_list(${quoteIdentFor('sqlite', table)})`,
      params: [],
    }).then(async (res) => {
      const raw = (res?.rows || []).map((row) => ({
        name: `fk_${row[0]}`,
        seq: Number(row[1]) || 0,
        refTable: row[2] == null ? null : String(row[2]),
        column: row[3] == null ? null : String(row[3]),
        refColumn: row[4] == null ? null : String(row[4]),
      }));
      const needsPk = [...new Set(raw.filter((r) => !r.refColumn && r.refTable).map((r) => r.refTable))];
      const pkByTable = new Map();
      for (const t of needsPk) {
        // eslint-disable-next-line no-await-in-loop -- one implicit-PK target is the norm; parallelising costs more than it saves
        const info = await invoke(DB_QUERY_COMMANDS.sqlite, {
          extensionId,
          connId,
          sql: `PRAGMA table_info(${quoteIdentFor('sqlite', t)})`,
          params: [],
        }).catch(() => null);
        const pks = (info?.rows || [])
          .filter((row) => Number(row[5]) > 0)
          .sort((a, b) => Number(a[5]) - Number(b[5]))
          .map((row) => String(row[1]));
        pkByTable.set(t, pks);
      }
      const resolved = raw
        .sort((a, b) => a.name.localeCompare(b.name) || a.seq - b.seq)
        .map((r) => ({
          ...r,
          refDb: null,
          refColumn: r.refColumn || (pkByTable.get(r.refTable) || [])[r.seq] || null,
        }));
      return groupForeignKeyRows(resolved);
    });
  } else if (engine === 'postgres') {
    // conkey and confkey are parallel arrays; unnesting them TOGETHER with
    // ordinality is what keeps column i paired with referenced column i. The
    // information_schema route (constraint_column_usage) does not preserve that
    // pairing and mis-orders composite keys.
    promise = invoke(DB_QUERY_COMMANDS.postgres, {
      extensionId,
      connId,
      sql: `SELECT con.conname, att.attname, fns.nspname, fcl.relname, fatt.attname
            FROM pg_constraint con
            JOIN pg_class cl ON cl.oid = con.conrelid
            JOIN pg_namespace ns ON ns.oid = cl.relnamespace
            JOIN pg_class fcl ON fcl.oid = con.confrelid
            JOIN pg_namespace fns ON fns.oid = fcl.relnamespace
            CROSS JOIN LATERAL unnest(con.conkey, con.confkey) WITH ORDINALITY AS k(attnum, fattnum, ord)
            JOIN pg_attribute att ON att.attrelid = con.conrelid AND att.attnum = k.attnum
            JOIN pg_attribute fatt ON fatt.attrelid = con.confrelid AND fatt.attnum = k.fattnum
            WHERE con.contype = 'f' AND ns.nspname = ${dbName ? '$1' : 'current_schema()'} AND cl.relname = ${dbName ? '$2' : '$1'}
            ORDER BY con.conname, k.ord`,
      params: dbName ? [dbName, table] : [table],
    }).then((res) =>
      groupForeignKeyRows(
        (res?.rows || []).map((row) => ({
          name: row[0] == null ? null : String(row[0]),
          column: row[1] == null ? null : String(row[1]),
          refDb: row[2] == null ? null : String(row[2]),
          refTable: row[3] == null ? null : String(row[3]),
          refColumn: row[4] == null ? null : String(row[4]),
        }))
      )
    );
  } else {
    promise = invoke(DB_QUERY_COMMANDS.mysql, {
      extensionId,
      connId,
      sql: `SELECT CONSTRAINT_NAME, COLUMN_NAME, REFERENCED_TABLE_SCHEMA, REFERENCED_TABLE_NAME, REFERENCED_COLUMN_NAME
            FROM information_schema.KEY_COLUMN_USAGE
            WHERE TABLE_SCHEMA = ${dbName ? '?' : 'DATABASE()'} AND TABLE_NAME = ? AND REFERENCED_TABLE_NAME IS NOT NULL
            ORDER BY CONSTRAINT_NAME, ORDINAL_POSITION`,
      params: dbName ? [dbName, table] : [table],
    }).then((res) =>
      groupForeignKeyRows(
        (res?.rows || []).map((row) => ({
          name: row[0] == null ? null : String(row[0]),
          column: row[1] == null ? null : String(row[1]),
          refDb: row[2] == null ? null : String(row[2]),
          refTable: row[3] == null ? null : String(row[3]),
          refColumn: row[4] == null ? null : String(row[4]),
        }))
      )
    );
  }

  promise = promise.catch(() => []);
  entry.foreignKeys.set(table, promise);
  return promise;
}
